import {
    ClipboardList,
    MessageCircle,
    MoreVertical,
    Edit,
    Play,
    Archive,
    Trash2,
    FileTextIcon,
    ListChecksIcon,
    Copy,
} from "lucide-react";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { Assessment, FormAssessment, InterviewAssessment } from "@type/assessments";
import { formatDuration, formatHumanReadable } from "@util/date";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./dialog";
import { useState } from "react";
import { deleteAssessment, duplicateAssessment, updateStatusAssessment } from "@service/assessment";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import { cn } from "@util/tailwind";

interface AssessmentCardProps {
    assessment: Assessment;
    onReload: () => void;
}

export default function AssessmentCard(props: AssessmentCardProps) {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const { isWorkspaceRestricted, isAssessmentActionBlocked , maintenanceTooltip} = useSubscription();
    const expiredTooltip = maintenanceTooltip
        ?? (isWorkspaceRestricted
            ? "Your trial has expired. Upgrade to manage screenings."
            : undefined);
    // Editing an assessment is additionally blocked when the AI-assessment
    // quota is exhausted (not just past-grace) — same gate as the editor save.
    const editBlockedTooltip = maintenanceTooltip
        ?? (isAssessmentActionBlocked
            ? "Your trial has expired or AI screening limit reached. Upgrade to edit screenings."
            : undefined);
    const isFormAssessment = props.assessment.type === "Form";
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState<boolean>(false);
    const [isLoadingDuplicate, setIsLoadingDuplicate] = useState<boolean>(false);

    const confirmDeleteAssessment = async () => {
        try {
            const response = await deleteAssessment(props.assessment.id as string);

            if (response.success) {
                snackbar.showSuccess("Screening deleted successfully.");
                props.onReload();
            } else {
                snackbar.showError(response.message || "Failed to delete screening.");
            }
        } catch (error) {
            catchErrorLog(`Error deleting assessment: ${error}`);
            snackbar.showError("An unexpected error occurred.");
        } finally {
            setDeleteConfirmOpen(false);
        }
    };

    const handleStatusChange = async (id: string, newStatus: string) => {
        try {
            // Update the assessment in the backend
            const response = await updateStatusAssessment(id, newStatus as "Active" | "Draft");
            if (response.status === 200) {
                snackbar.showSuccess(`Screening status updated to ${newStatus}.`);

                // Refresh the assessments list
                props.onReload();
            } else {
                snackbar.showError(response.message || "Failed to update screening status.");
            }
        } catch (error) {
            catchErrorLog(`Error updating assessment status: ${error}`);
            snackbar.showError("An unexpected error occurred.");
        }
    };


    const confirmDuplicateAssessment = async () => {
        setIsLoadingDuplicate(true);
        try {
            const response = await duplicateAssessment(props.assessment.id as string);
            if (response.success) {
                snackbar.showSuccess("Screening duplicated successfully.");
                props.onReload();
            } else {
                snackbar.showError(response.message || "Failed to duplicate screening.");
            }
        } catch (error) {
            catchErrorLog(`Error duplicating assessment: ${error}`);
            snackbar.showError("An unexpected error occurred.");
        } finally {
            setIsLoadingDuplicate(false);
            setDuplicateConfirmOpen(false);
        }
    };

    const getIconComponent = () => {
        switch (props.assessment.type) {
            case "Form":
                return ClipboardList;
            case "Official Interview":
            case "Interview":
                return MessageCircle;
            case "Practice Interview":
                return MessageCircle;
            default:
                return ClipboardList;
        }
    };

    const IconComponent = getIconComponent();

    // Get icon styling based on assessment type
    const getIconStyling = () => {
        if (isFormAssessment) {
            return {
                containerClass:
                    "w-8 h-8 bg-purple-50 rounded flex items-center justify-center",
                iconClass: "w-4 h-4 text-purple-600",
            };
        } else {
            // Interview assessments (Official Interview, Practice Interview)
            return {
                containerClass:
                    "w-8 h-8 bg-blue-50 rounded flex items-center justify-center",
                iconClass: "w-4 h-4 text-blue-600",
            };
        }
    };

    const iconStyling = getIconStyling();

    return (
        <>
            <div className={cn("bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow", {
                'cursor-pointer': userInfo?.permission === "member",
            })} onClick={() => {
                if (userInfo?.permission === "member") {
                    if (isFormAssessment) {
                        navigate(`/assess/${props.assessment.id}/form`);
                    } else {
                        navigate(`/assess/${props.assessment.id}/interview`);
                    }
                }
            }}>
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                        <div className={iconStyling.containerClass}>
                            <IconComponent className={iconStyling.iconClass} />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        {/* Hide Default chip for now */}
                        <Badge
                            variant={props.assessment.status === "Active" ? "default" : "secondary"}
                            className={
                                props.assessment.status === "Active"
                                    ? "bg-green-100 text-green-700 hover:bg-green-100 shadow-none font-medium"
                                    : "bg-yellow-100 text-yellow-700 hover:bg-yellow-100 shadow-none font-medium"
                            }
                            style={{
                                paddingTop: "4px",
                                paddingBottom: "4px",
                                borderRadius: "1rem",
                            }}
                        >
                            {props.assessment.status}
                        </Badge>
                        {(userInfo?.role === "admin" || userInfo?.role === "super_admin") && props.assessment.company_name && (
                            <Badge
                                variant="secondary"
                                className="bg-blue-100 text-blue-700 hover:bg-blue-100 shadow-none font-medium"
                                style={{
                                    paddingTop: "4px",
                                    paddingBottom: "4px",
                                    borderRadius: "1rem",
                                }}
                            >
                                {props.assessment.company_name}
                            </Badge>
                        )}
                        {userInfo?.permission !== "member" && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                        <MoreVertical className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => {
                                        if (isFormAssessment) {
                                            navigate(`/assess/${props.assessment.id}/form?tab=jobs`);
                                        } else {
                                            navigate(`/assess/${props.assessment.id}/interview?tab=jobs`);
                                        }
                                    }}>
                                        <ListChecksIcon className="w-4 h-4 mr-2" />
                                        View submissions
                                    </DropdownMenuItem>
                                    {!props.assessment.is_default && (
                                        <DropdownMenuItem
                                            onClick={() => {
                                                if (isFormAssessment) {
                                                    navigate(`/assess/${props.assessment.id}/form`);
                                                } else {
                                                    navigate(`/assess/${props.assessment.id}/interview`);
                                                }
                                            }}
                                            disabled={isAssessmentActionBlocked}
                                            title={editBlockedTooltip}
                                            className="disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Edit className="w-4 h-4 mr-2" />
                                            {isFormAssessment ? "Edit form" : "Edit interview"}
                                        </DropdownMenuItem>
                                    )}
                                    {props.assessment.status === "Active" && (
                                        <DropdownMenuItem
                                            onClick={() => setDuplicateConfirmOpen(true)}
                                            disabled={isWorkspaceRestricted}
                                            title={expiredTooltip}
                                            className="disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Copy className="w-4 h-4 mr-2" />
                                            Duplicate
                                        </DropdownMenuItem>
                                    )}
                                    {props.assessment.status === "Active" ? (
                                        !props.assessment.is_default && (
                                            <DropdownMenuItem
                                                onClick={() => handleStatusChange(props.assessment.id as string, "Draft")}
                                                disabled={isWorkspaceRestricted}
                                                title={expiredTooltip}
                                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Archive className="w-4 h-4 mr-2" />
                                                Convert to Draft
                                            </DropdownMenuItem>
                                        )
                                    ) : (
                                        <>
                                            <DropdownMenuItem
                                                onClick={() => handleStatusChange(props.assessment.id as string, "Active")}
                                                disabled={isWorkspaceRestricted}
                                                title={expiredTooltip}
                                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Play className="w-4 h-4 mr-2" />
                                                Activate interview
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    {!props.assessment.is_default && (
                                        <DropdownMenuItem
                                            onClick={() => setDeleteConfirmOpen(true)}
                                            disabled={isWorkspaceRestricted}
                                            title={expiredTooltip}
                                            className="text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            {isFormAssessment ? "Delete form" : "Delete interview"}
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                        {isFormAssessment ? (props.assessment as FormAssessment).content.title : (props.assessment as InterviewAssessment).content.interviewName}
                    </h3>
                    <p className="text-sm font-normal text-gray-500">
                        {props.assessment.type === "Form"
                            ? "Form"
                            : props.assessment.type === "Interview" ? "AI Interview"
                                : props.assessment.type}
                    </p>
                    <div className="flex items-center justify-between text-sm font-normal text-gray-500">
                        <span>
                            {isFormAssessment
                                ? `${(props.assessment as FormAssessment).content.questions?.length || 0} questions`
                                : `${(props.assessment as InterviewAssessment).content.sections?.length || 0} sections • ${formatDuration((props.assessment as InterviewAssessment).content.maxDuration || 0, "mins")}`}
                        </span>
                        <span className="text-sm font-normal text-gray-500">
                            {props.assessment.id
                                ? formatHumanReadable(props.assessment.updated_at?.slice(0, props.assessment.updated_at.length - 1) as string, true)
                                : "New"}
                        </span>
                    </div>
                </div>
            </div>
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Screening</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this screening? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={confirmDeleteAssessment}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={duplicateConfirmOpen} onOpenChange={setDuplicateConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Duplicate Screening</DialogTitle>
                        <DialogDescription>
                            A copy of this screening will be created as a Draft. You can then edit it to fit your needs.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDuplicateConfirmOpen(false)} disabled={isLoadingDuplicate}>
                            Cancel
                        </Button>
                        <Button onClick={confirmDuplicateAssessment} disabled={isLoadingDuplicate} className="bg-blue-600 hover:bg-blue-700">
                            {isLoadingDuplicate ? "Duplicating..." : "Duplicate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
